import {
  Component,
  Input,
  OnInit,
  Signal,
  ViewChild,
  computed,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import {
  LogService,
  RecentClaim,
  RecentClaimService,
  UserRoleService,
} from 'src/app/my-sedgwick';
import {
  BaseComponent,
  ListComponent,
  SnackbarService,
  SnackbarType,
} from '../../../shared';
import { UserActions } from '../../../shared/models/user';
import { Session } from '../../services/session.service';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { ClaimKey } from 'src/app/shared/models/claim-key';

@Component({
    selector: 'app-watch-list',
    template: `
    <app-panel
      [title]="'Watch List'"
      [headerLevel]="overlay ? 2 : 1"
      [minHeight]="minHeightPnl"
      [canExpand]="false"
      [attr.role]="overlay ? 'dialog' : 'null'"
      [ariaLabel]="'Watch list'"
      [attr.role]="overlay ? 'dialog' : 'null'"
      [attr.aria-modal]="overlay ? true : false"
      >
      <app-loading-panel [state]="isLoading ? 'Loading' : 'Loaded'">
        <ng-template #content> </ng-template>
      </app-loading-panel>
    
      <app-list
        #table
        [dataSource]="
          overlay && isController
            ? loadOverlayControllerWatch()
            : recentClaimService.watchList()
        "
        [template]="row"
        [DefaultSortColumn]="isSupervisorRole ? 'openDate' : ''"
        [DefaultSortDir]="isSupervisorRole ? 'desc' : ''"
        class="app-pt2 app-rl8-selectall"
        [ShowSort]="overlay || claimsLength() === 0 ? false : true"
        [filterEnabled]="false"
        loadingMessage=""
        [noDataIcon]="isLoading ? '' : 'star'"
        [noDataHeader]="
          isLoading ? '' : 'No claims have been added to the watch list'
        "
        [noDataMessage]="
          isLoading
            ? ''
            : 'To add claims, click the star icon next to the Claim value in any list or page'
        "
        [loaded]="isLoading"
        [paginatorEnabled]="false"
        [showRowSelection]="overlay || claimsLength() === 0 ? false : true"
        [pageSize]="overlay ? 3 : 25"
        [overlay]="overlay"
        [ariaLabelSelect]="'Select this claim to remove from watchlist'"
        [ariaLabelSelectAll]="'Select all claims to remove to watchlist'"
        [tableAriaLabel]="'Watchlist'"
        >
        <ng-container header>
          <div class="tw-flex tw-flex-wrap ">
            <app-list-header
              name="claimNumber"
              label="Claim"
              class="tw-w-full md:tw-w-[40%] header-value"
              [names]="['claimDescription']"
              [sortEnabled]="!overlay"
            ></app-list-header>
            <app-list-header
              name="name"
              label="Name"
              class="tw-w-full md:tw-w-[40%] header-value"
              [names]="['name', 'employeeID']"
              [sortEnabled]="!overlay"
            ></app-list-header>
            <app-list-header
              name="openDate"
              label="Date added"
              filterType="Date"
              [sortEnabled]="!overlay"
              class="tw-w-full md:tw-w-[20%] header-value"
            ></app-list-header>
          </div>
        </ng-container>
      </app-list>
    
      <ng-template #row let-element role="rowgroup">
        <div class="tw-flex tw-flex-wrap tw-w-full tw-items-center">
          <td class="tw-w-full md:tw-w-[40%]">
            <app-value label="Claim" layout="row-xs" [translationNeeded]="false" [valueTemplate]="claimLabelTpl">
              <ng-template #claimLabelTpl>
                <app-claim-label
                  [claim]="element"
                  layout="row"
                  [hideWatchList]="true"
                  >
                </app-claim-label>
              </ng-template>
            </app-value>
          </td>
          <td class="tw-w-full md:tw-w-[40%]">
            <app-value label="Name" layout="row-xs" [valueTemplate]="claimantLabelTpl">
              <ng-template #claimantLabelTpl>
                <app-claimant-label
                  [claimant]="element"
                  [disabled]="!userRole.manager"
                  layout="row"
                  >
                </app-claimant-label>
              </ng-template>
            </app-value>
          </td>
          <td class="tw-w-full md:tw-w-[10%]">
            <app-value
              [tabindex]="element.openDate ? '0' : '1'"
              label="Date Added"
              [value]="element.openDate | format : 'date'"
              layout="row-xs"
              [translationNeeded]="false"
              >
            </app-value>
          </td>
          <div class="tw-w-full md:tw-hidden tw-block app-pb1">
            <a
              class="tw-block md:tw-hidden"
              (click)="deleteSelectedWatchList([element])"
              tabindex="0"
              >
              <b>{{ 'Remove' | translation:translationRequest}}</b>
            </a>
          </div>
        </div>
      </ng-template>
      <div class="md:tw-block tw-hidden">
        @if (!overlay && !(claimsLength() === 0)) {
          <app-button
            emphasis="Low"
            (click)="deleteSelectedWatchList(table.selectedItems)"
            label="Remove"
            >
          </app-button>
        }
      </div>
      @if (overlay && !(claimsLength() === 0)) {
        <a routerLink="watchlist"
          >{{ 'View All' | translation: translationRequest }}</a
          >
        }
      </app-panel>
    `,
    styles: [
        `
      .header-value {
        position: relative;
        left: 8px;
        padding-top: 10px;
        margin-bottom: -10px;
      }
    `,
    ],
    standalone: false
})
export class WatchListComponent extends BaseComponent implements OnInit {
  @Input() overlay = false;

  @Input() translationRequest: TranslationRequest;

  @ViewChild('table', { static: false }) table: ListComponent;

  claims: RecentClaim[];
  isLoaded: boolean = false;
  isLoading: boolean = true;
  MaxWatchListCount: number;
  minHeightPnl: string = '70px';
  isSupervisorRole: boolean = false;
  isController: boolean = false;
  mgrAction: UserActions | null;
  watchListClaims: Signal<RecentClaim[]>;

  claimsLength = computed(() => {
    if (this.watchListClaims) {
      return this.watchListClaims()?.length;
    } else {
      return 0;
    }
  });

  constructor(
    public recentClaimService: RecentClaimService,
    public userRole: UserRoleService,
    public logService: LogService,
    private session: Session,
    private snackBarService: SnackbarService,
    private el: ElementRef
  ) {
    super();
  }

  waitForData() {
    if (this.table) {
      this.table.updateDataSource();
      this.isLoading = false;
    } else {
      this.isLoading = true;
      setTimeout(() => {
        this.waitForData();
      }, 500);
    }
  }

  async ngOnInit() {
    this.isController = this.userRole.controller;
    this.isSupervisorRole = this.userRole.manager;
    this.mgrAction =
      this.isSupervisorRole || this.isController
        ? UserActions.ViewOwnMgrDb
        : null;
    this.watchListClaims = this.recentClaimService.watchList;
    this.waitForData();
  }

  async deleteSelectedWatchList(items: RecentClaim[]) {
    if (items.length == 0) {
      return;
    }
    if (
      this.session.user.emulatorContext &&
      this.session.user.emulatorContext.isEmulating
    ) {
      this.snackBarService.show(
        'Preview-only mode. Information not sent.',
        SnackbarType.Info
      );
    } else {
      const ClaimKeyList: { claimId: string; source: string }[] = [];

      for (let i = 0; i < items.length; i++) {
        ClaimKeyList.push({
          claimId: items[i].claimId,
          source: items[i].source,
        });
      }

      await this.recentClaimService.deleteWatchList(ClaimKeyList);
    }
    this.isLoaded = true;
  }

  loadOverlayControllerWatch() {
    const items: RecentClaim[] = [];
    if (
      this.recentClaimService.watchList() != undefined &&
      this.recentClaimService.watchList().length > 0
    ) {
      this.recentClaimService.watchList().forEach((val, ind) => {
        if (ind < 3) {
          items.push(val);
        }
      });
    }
    return items;
  }
}
